import type { Region } from '~~/core/entities/Region';

import { PropertyFiltersPresenter } from '~/presenters/PropertyFiltersPresenter';
import { slugify } from '~~/utils';
import { REGION_SERVICE } from '~~/utils/symbols';

const defaultAllowedFilters = [
	'regions',
	'cities',
	'states',
	'locationSlug',
	'transactionType',
	'macroregions',
	'bounds',
	'zoom',
	'commercialIds'
];

export function useRegionInfos(
	filters: Ref<{
		regions?: string[];
		cities?: string[];
		states?: string[];
	}>
) {
	const route = useRoute();
	const regionService = inject(REGION_SERVICE)!;

	const key = computed(
		() =>
			`region:${filters.value.regions?.[0]}${filters.value.cities?.[0]}${filters.value.states?.[0]}`
	);

	const regionName = computed<string>(() => {
		return (
			regionInfo.value?.hero?.title ||
			regionInfo.value?.name ||
			filters.value.regions?.[0] ||
			''
		);
	});

	const hasInvalidFiltersToShowRegionInfo = () => {
		const filtersFromPath = PropertyFiltersPresenter.getFiltersFromPath(
			route.fullPath
		);

		const allowedFilters = [...defaultAllowedFilters, 'propertyTypes'];

		const hasInvalidFilter = Object.entries(filtersFromPath).some(
			([key, value]) =>
				!allowedFilters.includes(key) &&
				(Array.isArray(value) ? value.length > 0 : Boolean(value))
		);

		return hasInvalidFilter;
	};

	const hasFiltersIncludingNotAllowedFilters = () => {
		const filtersFromPath = PropertyFiltersPresenter.getFiltersFromPath(
			route.fullPath
		);
		const allowedFilters = [...defaultAllowedFilters, 'propertyTypes'];
		const hasNotAllowedFilters = Object.entries(filtersFromPath).some(
			([key, value]) => {
				const isNotAllowed = !allowedFilters.includes(key);
				const hasValue = Array.isArray(value)
					? value.length > 0
					: Boolean(value);

				return isNotAllowed && hasValue;
			}
		);

		return hasNotAllowedFilters;
	};

	const { data: regionInfo, refresh: refreshRegionInfo } =
		useAsyncData<Region | null>(
			key.value,
			async () => {
				const slug = slugify(filters.value.regions?.[0] || '');
				if (
					!slug ||
					hasFiltersIncludingNotAllowedFilters() ||
					hasInvalidFiltersToShowRegionInfo()
				) {
					return null;
				}

				const [region] = await regionService.getOne({ slug });
				return region;
			},
			{
				default: () => null,
				...getCacheAsyncDataConfig<Region | null>(30 * 60 * 1000),
				lazy: false,
				server: true,
				immediate: true,
				watch: [() => filters.value.regions]
			}
		);

	return {
		regionInfo,
		refreshRegionInfo,
		regionName,
		defaultAllowedFilters
	};
}
